import api from './api';
import {
  Folder,
  FolderContents,
  Breadcrumb,
  FolderTree,
  FolderShare,
  SharedFolder,
  StorageQuota,
  TrashContents,
  FolderCreateData,
  FolderUpdateData,
  ShareCreateData,
  FileDocument,
  SearchResultItem,
  SearchResults,
} from '../types/files';

// Endpoints
const ENDPOINTS = {
  folders: '/folders',
  folderById: (id: number) => `/folders/${id}`,
  folderContents: (id: number) => `/folders/${id}/contents`,
  folderBreadcrumb: (id: number) => `/folders/${id}/breadcrumb`,
  folderMove: (id: number) => `/folders/${id}/move`,
  folderDuplicate: (id: number) => `/folders/${id}/duplicate`,
  folderFavorite: (id: number) => `/folders/${id}/favorite`,
  folderShare: (id: number) => `/folders/${id}/share`,
  folderShares: (id: number) => `/folders/${id}/shares`,
  folderShareById: (folderId: number, shareId: number) => `/folders/${folderId}/shares/${shareId}`,
  folderActivity: (id: number) => `/folders/${id}/activity`,
  folderTree: '/folders/tree',
  folderSearch: '/folders/search',
  sharedWithMe: '/folders/shared-with-me',
  foldersFavorites: '/folders/favorites',
  documentsFavorites: '/documents/favorites',
  documentsRecent: '/documents/recent',
  trash: '/trash',
  trashRestore: '/trash/restore',
  trashPermanentDelete: '/trash/permanent-delete',
  trashEmpty: '/trash/empty',
  trashStats: '/trash/stats',
  storageQuota: '/storage/quota',
  storageCheck: '/storage/check',
  documents: '/documents',
  documentUpload: '/documents/upload',
  documentById: (id: number) => `/documents/${id}`,
  documentDownload: (id: number) => `/documents/${id}/download`,
  documentFavorite: (id: number) => `/documents/${id}/favorite`,
  documentSearch: '/documents/search',
};

// ============== FOLDERS ==============

export const getFolders = async (parentId?: number | null, egliseId?: number): Promise<Folder[]> => {
  const params: Record<string, any> = {};
  if (parentId !== undefined) params.parent_id = parentId;
  if (egliseId) params.eglise_id = egliseId;

  const response = await api.get<Folder[]>(ENDPOINTS.folders, { params });
  return response.data;
};

export const getFolderById = async (id: number): Promise<Folder> => {
  const response = await api.get<Folder>(ENDPOINTS.folderById(id));
  return response.data;
};

export const getFolderContents = async (
  folderId: number,
  includeDeleted: boolean = false
): Promise<FolderContents> => {
  const response = await api.get<FolderContents>(ENDPOINTS.folderContents(folderId), {
    params: { include_deleted: includeDeleted },
  });
  return response.data;
};

export const getRootContents = async (): Promise<FolderContents> => {
  // Get root folders and documents (parent_id = null)
  const folders = await getFolders(null);
  return {
    folder_id: null,
    folders,
    documents: [],
    total_folders: folders.length,
    total_documents: 0,
  };
};

export const getBreadcrumb = async (folderId: number): Promise<Breadcrumb[]> => {
  const response = await api.get<Breadcrumb[]>(ENDPOINTS.folderBreadcrumb(folderId));
  return response.data;
};

export const getFolderTree = async (rootId?: number, maxDepth: number = 5): Promise<FolderTree[]> => {
  const params: Record<string, any> = { max_depth: maxDepth };
  if (rootId) params.root_id = rootId;

  const response = await api.get<FolderTree[]>(ENDPOINTS.folderTree, { params });
  return response.data;
};

export const searchFolders = async (
  query: string,
  egliseId?: number,
  limit: number = 50
): Promise<{ results: SearchResultItem[]; count: number }> => {
  const params: Record<string, any> = { q: query, limit };
  if (egliseId) params.eglise_id = egliseId;

  const response = await api.get<{ success: boolean; query: string; count: number; results: SearchResultItem[] }>(
    ENDPOINTS.folderSearch,
    { params }
  );
  return { results: response.data.results, count: response.data.count };
};

export const searchDocuments = async (
  query: string,
  limit: number = 50
): Promise<{ results: SearchResultItem[]; count: number }> => {
  const params: Record<string, any> = { q: query, limit };

  const response = await api.get<{ success: boolean; query: string; count: number; results: SearchResultItem[] }>(
    ENDPOINTS.documentSearch,
    { params }
  );

  // Map document results to SearchResultItem format
  const results: SearchResultItem[] = response.data.results.map((doc: any) => ({
    id: doc.id,
    type: 'document' as const,
    name: doc.title,
    filename: doc.filename,
    file_url: doc.file_url,
    file_size: doc.file_size,
    file_type: doc.file_type,
    folder_id: doc.folder_id,
    parent_path: doc.parent_path,
    parent_name: doc.parent_name,
    created_at: doc.created_at,
    updated_at: doc.updated_at,
  }));

  return { results, count: response.data.count };
};

// Global search - searches both folders and documents in parallel
export const globalSearch = async (query: string, limit: number = 50): Promise<SearchResults> => {
  const [foldersResult, documentsResult] = await Promise.all([
    searchFolders(query, undefined, limit),
    searchDocuments(query, limit),
  ]);

  return {
    query,
    folders: foldersResult.results,
    documents: documentsResult.results,
    total_count: foldersResult.count + documentsResult.count,
  };
};

export const createFolder = async (data: FolderCreateData): Promise<Folder> => {
  const response = await api.post<Folder>(ENDPOINTS.folders, data);
  return response.data;
};

export const updateFolder = async (id: number, data: FolderUpdateData): Promise<Folder> => {
  const response = await api.put<Folder>(ENDPOINTS.folderById(id), data);
  return response.data;
};

export const moveFolder = async (id: number, newParentId: number | null): Promise<Folder> => {
  const response = await api.post<Folder>(ENDPOINTS.folderMove(id), {
    new_parent_id: newParentId,
  });
  return response.data;
};

export const duplicateFolder = async (id: number, newName?: string): Promise<Folder> => {
  const params: Record<string, any> = {};
  if (newName) params.new_name = newName;

  const response = await api.post<Folder>(ENDPOINTS.folderDuplicate(id), null, { params });
  return response.data;
};

export const deleteFolder = async (id: number, permanent: boolean = false): Promise<void> => {
  await api.delete(ENDPOINTS.folderById(id), {
    params: { permanent },
  });
};

// ============== FAVORITES ==============

export const addFolderToFavorites = async (id: number): Promise<void> => {
  await api.post(ENDPOINTS.folderFavorite(id));
};

export const removeFolderFromFavorites = async (id: number): Promise<void> => {
  await api.delete(ENDPOINTS.folderFavorite(id));
};

export const addDocumentToFavorites = async (id: number): Promise<void> => {
  await api.post(ENDPOINTS.documentFavorite(id));
};

export const removeDocumentFromFavorites = async (id: number): Promise<void> => {
  await api.delete(ENDPOINTS.documentFavorite(id));
};

export const getFavoriteFolders = async (): Promise<Folder[]> => {
  const response = await api.get<Folder[]>(ENDPOINTS.foldersFavorites);
  return response.data;
};

export const getFavoriteDocuments = async (): Promise<FileDocument[]> => {
  const response = await api.get<FileDocument[]>(ENDPOINTS.documentsFavorites);
  return response.data;
};

export const getRecentDocuments = async (limit: number = 20): Promise<FileDocument[]> => {
  const response = await api.get<FileDocument[]>(ENDPOINTS.documentsRecent, {
    params: { limit },
  });
  return response.data;
};

// ============== SHARING ==============

export const getSharedWithMe = async (): Promise<SharedFolder[]> => {
  const response = await api.get<SharedFolder[]>(ENDPOINTS.sharedWithMe);
  return response.data;
};

export const getFolderShares = async (folderId: number): Promise<FolderShare[]> => {
  const response = await api.get<FolderShare[]>(ENDPOINTS.folderShares(folderId));
  return response.data;
};

export const shareFolder = async (folderId: number, data: ShareCreateData): Promise<FolderShare> => {
  const response = await api.post<FolderShare>(ENDPOINTS.folderShare(folderId), data);
  return response.data;
};

export const updateShare = async (
  folderId: number,
  shareId: number,
  permission: 'read' | 'write' | 'admin'
): Promise<FolderShare> => {
  const response = await api.put<FolderShare>(ENDPOINTS.folderShareById(folderId, shareId), {
    permission,
  });
  return response.data;
};

export const revokeShare = async (folderId: number, shareId: number): Promise<void> => {
  await api.delete(ENDPOINTS.folderShareById(folderId, shareId));
};

// ============== TRASH ==============

export const getTrashContents = async (
  itemType?: 'folder' | 'document'
): Promise<TrashContents> => {
  const params: Record<string, any> = {};
  if (itemType) params.item_type = itemType;

  const response = await api.get<TrashContents>(ENDPOINTS.trash, { params });
  return response.data;
};

export const restoreFromTrash = async (
  itemType: 'folder' | 'document',
  itemId: number
): Promise<void> => {
  await api.post(ENDPOINTS.trashRestore, {
    item_type: itemType,
    item_id: itemId,
  });
};

export const permanentDelete = async (
  itemType: 'folder' | 'document',
  itemId: number
): Promise<void> => {
  await api.post(ENDPOINTS.trashPermanentDelete, {
    item_type: itemType,
    item_id: itemId,
    confirm: true,
  });
};

export const emptyTrash = async (): Promise<{ deleted_folders: number; deleted_documents: number }> => {
  const response = await api.delete<{ deleted_folders: number; deleted_documents: number }>(
    ENDPOINTS.trashEmpty,
    { params: { confirm: true } }
  );
  return response.data;
};

// ============== STORAGE ==============

export const getStorageQuota = async (egliseId?: number): Promise<StorageQuota> => {
  const params: Record<string, any> = {};
  if (egliseId) params.eglise_id = egliseId;

  const response = await api.get<StorageQuota>(ENDPOINTS.storageQuota, { params });
  return response.data;
};

export const checkUploadQuota = async (
  fileSize: number,
  egliseId?: number
): Promise<{ can_upload: boolean; reason?: string; message?: string }> => {
  const params: Record<string, any> = { file_size: fileSize };
  if (egliseId) params.eglise_id = egliseId;

  const response = await api.get(ENDPOINTS.storageCheck, { params });
  return response.data;
};

// ============== DOCUMENTS ==============

export const uploadDocument = async (
  file: File,
  folderId?: number | null,
  onProgress?: (progress: number) => void
): Promise<FileDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', file.name);
  if (folderId) {
    formData.append('folder_id', folderId.toString());
  }

  const response = await api.post<FileDocument>(ENDPOINTS.documentUpload, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // 2 minutes for large files
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });
  return response.data;
};

export const deleteDocument = async (id: number): Promise<void> => {
  await api.delete(ENDPOINTS.documentById(id));
};

export const moveDocument = async (id: number, newFolderId: number | null): Promise<FileDocument> => {
  const response = await api.put<FileDocument>(ENDPOINTS.documentById(id), {
    folder_id: newFolderId,
  });
  return response.data;
};

export const downloadDocument = async (id: number): Promise<Blob> => {
  const response = await api.get(ENDPOINTS.documentDownload(id), {
    responseType: 'blob',
  });
  return response.data;
};

// ============== ACTIVITY ==============

export const getFolderActivity = async (
  folderId: number,
  limit: number = 50,
  offset: number = 0
): Promise<any[]> => {
  const response = await api.get(ENDPOINTS.folderActivity(folderId), {
    params: { limit, offset, include_children: true },
  });
  return response.data;
};

// ============== UTILITIES ==============

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const getFileIcon = (mimeType: string): string => {
  if (!mimeType) return 'description';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'movie';
  if (mimeType.startsWith('audio/')) return 'audiotrack';
  if (mimeType === 'application/pdf') return 'picture_as_pdf';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'table_chart';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'slideshow';
  if (mimeType.includes('document') || mimeType.includes('word')) return 'article';
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('archive')) return 'folder_zip';
  return 'description';
};

export const getFileTypeLabel = (mimeType: string): string => {
  if (!mimeType) return 'File';
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType.startsWith('video/')) return 'Video';
  if (mimeType.startsWith('audio/')) return 'Audio';
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'Spreadsheet';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'Presentation';
  if (mimeType.includes('document') || mimeType.includes('word')) return 'Document';
  if (mimeType.includes('zip') || mimeType.includes('rar')) return 'Archive';
  return 'File';
};

export const isPreviewable = (mimeType: string): boolean => {
  if (!mimeType) return false;
  return (
    mimeType.startsWith('image/') ||
    mimeType === 'application/pdf' ||
    mimeType.startsWith('video/') ||
    mimeType.startsWith('audio/')
  );
};

export default {
  // Folders
  getFolders,
  getFolderById,
  getFolderContents,
  getRootContents,
  getBreadcrumb,
  getFolderTree,
  searchFolders,
  searchDocuments,
  globalSearch,
  createFolder,
  updateFolder,
  moveFolder,
  duplicateFolder,
  deleteFolder,
  // Favorites
  addFolderToFavorites,
  removeFolderFromFavorites,
  addDocumentToFavorites,
  removeDocumentFromFavorites,
  getFavoriteFolders,
  getFavoriteDocuments,
  getRecentDocuments,
  // Sharing
  getSharedWithMe,
  getFolderShares,
  shareFolder,
  updateShare,
  revokeShare,
  // Trash
  getTrashContents,
  restoreFromTrash,
  permanentDelete,
  emptyTrash,
  // Storage
  getStorageQuota,
  checkUploadQuota,
  // Documents
  uploadDocument,
  deleteDocument,
  moveDocument,
  downloadDocument,
  // Activity
  getFolderActivity,
  // Utilities
  formatFileSize,
  getFileIcon,
  getFileTypeLabel,
  isPreviewable,
};
